import {Alert, Breadcrumb, Button, Col, Row, Tabs} from "antd";
import {DashboardWrapper} from "../../../../../layouts/dashboard-wrapper";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {useEffect, useState} from "react";
import {ErrorBoundary} from "react-error-boundary";
import {useGetEditableSubscriptionContext} from "../../../../../subscriptions-feature/useCases/edit-subscription/context/get-editable-subscription";
import {EditSubscriptionBasicForm} from "../../../../../subscriptions-feature/useCases/edit-subscription/basic-form";
import {EditSubscriptionCheckoutForm} from "../../../../../subscriptions-feature/useCases/edit-subscription/checkout-form";
import {EditSubscriptionCoverImagesForm} from "../../../../../subscriptions-feature/useCases/edit-subscription/cover-images-form";
import {ShareSubscriptionWidget} from "../../../../../components/store/subscriptions/share-subscription-widget";
import {OfferingAutomations} from "../../../../../components/offering-form/automations-step";
import {SubscriptionSalesAndInsights} from "../../../../../components/store/subscriptions/sales-and-insights";

type TabKeys =
  | "form"
  | "coverImages"
  | "automations"
  | "salesAndInsights"
  | "checkoutForm";

export function EditSubscriptionPage() {
  const {
    state: {subscriptionData, isFetchingSubscription},
  } = useGetEditableSubscriptionContext();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("activeTab");
  const [defaultActiveKey, setDefaultActiveKey] = useState<TabKeys>("form");
  const navigate = useNavigate();

  useEffect(() => {
    if (
      activeTab === "coverImages" ||
      activeTab === "checkoutForm" ||
      activeTab === "automations" ||
      activeTab === "salesAndInsights"
    ) {
      setDefaultActiveKey(activeTab);
    }
  }, [activeTab]);

  function handleTabClick(key: string) {
    if (key === "form") {
      setDefaultActiveKey("form");
      navigate(`${window.location.pathname}?activeTab=form`);
    }
    if (key === "coverImages") {
      setDefaultActiveKey("coverImages");
      navigate(`${window.location.pathname}?activeTab=coverImages`);
    }
    if (key === "automations") {
      setDefaultActiveKey("automations");
      navigate(`${window.location.pathname}?activeTab=automations`);
    }
    if (key === "checkoutForm") {
      setDefaultActiveKey("checkoutForm");
      navigate(`${window.location.pathname}?activeTab=checkoutForm`);
    }
    if (key === "salesAndInsights") {
      setDefaultActiveKey("salesAndInsights");
      navigate(`${window.location.pathname}?activeTab=salesAndInsights`);
    }
  }

  const tabFallback = ({
    error,
    resetErrorBoundary,
  }: {
    error: Error;
    resetErrorBoundary: () => void;
  }) => (
    <div style={{marginTop: "1.8rem"}}>
      <Alert
        message="Sorry, something went wrong."
        showIcon
        description={error.message}
        type="error"
        action={
          <Button onClick={() => resetErrorBoundary()} size="small" danger>
            Reset
          </Button>
        }
      />
    </div>
  );

  return (
    <DashboardWrapper pageTitle="Edit subscription">
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/products?activeTab=subscriptions">Subscription</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{subscriptionData.productSubscription.name}</Breadcrumb.Item>
      </Breadcrumb>
      <div style={{marginTop: "1.5rem"}}>
        <Row gutter={[36, 30]}>
          <Col span={24} lg={{span: 16}}>
            <Tabs
              defaultActiveKey={defaultActiveKey}
              activeKey={defaultActiveKey}
              onTabClick={(key) => handleTabClick(key)}
            >
              <Tabs.TabPane tab="Subscription info" key="form">
                <ErrorBoundary FallbackComponent={tabFallback}>
                  <EditSubscriptionBasicForm />
                </ErrorBoundary>
              </Tabs.TabPane>
              <Tabs.TabPane tab="Cover image" key="coverImages">
                <ErrorBoundary FallbackComponent={tabFallback}>
                  <EditSubscriptionCoverImagesForm />
                </ErrorBoundary>
              </Tabs.TabPane>
              <Tabs.TabPane tab="Automations" key="automations">
                <ErrorBoundary FallbackComponent={tabFallback}>
                  <OfferingAutomations
                    contextOfferingId={subscriptionData.productSubscription.id}
                  />
                </ErrorBoundary>
              </Tabs.TabPane>
              <Tabs.TabPane tab="Sales and insights" key="salesAndInsights">
                <ErrorBoundary FallbackComponent={tabFallback}>
                  <SubscriptionSalesAndInsights />
                </ErrorBoundary>
              </Tabs.TabPane>
              <Tabs.TabPane tab="Checkout form" key="checkoutForm">
                <ErrorBoundary FallbackComponent={tabFallback}>
                  <EditSubscriptionCheckoutForm />
                </ErrorBoundary>
              </Tabs.TabPane>
            </Tabs>
          </Col>
          <Col span={24} lg={{span: 8}}>
            <ErrorBoundary FallbackComponent={tabFallback}>
              <ShareSubscriptionWidget
                isFetching={isFetchingSubscription}
                subscriptionData={subscriptionData}
              />
            </ErrorBoundary>
          </Col>
        </Row>
      </div>
    </DashboardWrapper>
  );
}
